import { Typography, Stack, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { TableCellCustom } from '@app/components/table';
import { IAddProjectSchema } from '../types';
import { RowActions } from './row-actions';
import { FormProvider, RHFSelect, StatusTag, TruncatedTypography } from '@app/components';
import { CustomAvatar, CustomAvatarGroup } from '@app/components/custom-avatar';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { ProjectStatus, Strings } from '@app/constants';
import { useDialog, useRequestState } from '@app/hooks';
import { DeleteDialog } from '@app/components/dialog';
import { updateProjectStatus } from '../slice';
import { selectProjectState, selectUpdateProjectStatusState } from '../selector';
import { useAuthRole } from '@app/utils';

interface Props {
  row: IAddProjectSchema;
  selected: boolean;
  onSelectRow: VoidFunction;
  canAddProject: string;
}
const ProjectRow = (props: Props) => {
  const { row, canAddProject } = props;
  const { isManager, isSuperAdmin } = useAuthRole();

  const dispatch = useDispatch();

  const deleteDialog = useDialog();
  const methods = useForm();
  const updatedStatus = useWatch({ control: methods.control, name: 'updateStatus' });

  const onPressConfirm = () => {
    dispatch(updateProjectStatus({ ...row, status: updatedStatus }));
  };

  const { loading } = useRequestState({
    stateSelector: selectUpdateProjectStatusState,
    onSuccess: deleteDialog.hide,
    successMessageShown: true,
    errorShown: true,
  });

  const statusOptions = [
    {
      value: 'Draft',
      label: Strings.status.draft,
    },
    {
      value: 'InProgress',
      label: Strings.status.inProgress,
    },
    {
      value: 'Hold',
      label: Strings.status.hold,
    },
    {
      value: 'Delivered',
      label: Strings.status.delivered,
    },
  ];
  const statusMapping: Record<string, ProjectStatus> = {
    Delivered: ProjectStatus.Delivered,
    InProgress: ProjectStatus.InProgress,
    Hold: ProjectStatus.Hold,
    Draft: ProjectStatus.Draft,
  };
  const statusValue = statusMapping[row.status] || ProjectStatus.Draft;

  const navigate = useNavigate();
  const handleDetails = () => {
    navigate(`${PATH_DASHBOARD.projectDetail}/${row.name}`, { state: { id: row.id } });
  };
  const { data } = useRequestState({
    stateSelector: selectProjectState,
    errorShown: true,
  });

  const managers = row?.managers?.map((managerId: any) => {
    const manager = data?.users?.find((member: any) => member.id === managerId);
    return {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      name: `${manager?.firstName ?? ''} ${manager?.lastName ?? ''}`.trim(),
      profileImage: manager?.profileImage ?? '',
    };
  });

  const teamMember = row?.members?.map((memberId: any) => {
    const members: any = data?.users?.find((member: any) => member.id === memberId);

    return {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      name: `${members?.firstName ?? ''} ${members?.lastName ?? ''}`.trim(),
      profileImage: members?.profileImage ?? '',
    };
  });

  return (
    <>
      <TableCellCustom flex onClick={handleDetails}>
        <Typography variant="body2">{row.id}</Typography>
      </TableCellCustom>
      <TableCellCustom flex onClick={handleDetails}>
        <Stack style={{ flexDirection: 'row', alignItems: 'center', gap: '9px' }}>
          <CustomAvatar
            name={row?.name}
            src={row?.image}
            color={row?.image ? 'default' : 'info'}
            sx={{
              '& img': {
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              },
            }}
          />
          <Typography variant="body2">{row?.name}</Typography>
        </Stack>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <Typography variant="body2">
          {Array.isArray(row?.boardNames) && row.boardNames.length > 1
            ? row.boardNames.join(', ')
            : row?.boardNames}
        </Typography>
      </TableCellCustom>
      {(isManager || isSuperAdmin) && (
        <TableCellCustom flex align="center" onClick={handleDetails}>
          <Stack style={{ flexDirection: 'row', alignItems: 'center', gap: '9px' }}>
            <CustomAvatar
              name={`${row?.clients?.firstName} ${row?.clients?.lastName}` || 'NA'}
              color="info"
              src={row?.clients?.image}
            />
            <Typography variant="body2">{row?.clients?.firstName || 'NA'}</Typography>
          </Stack>
        </TableCellCustom>
      )}
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <CustomAvatarGroup max={3}>
          {managers?.length > 0 &&
            managers?.map((manager: any, index: number) => (
              <CustomAvatar
                key={index}
                name={manager?.name}
                color="info"
                src={manager?.profileImage}
              />
            ))}
        </CustomAvatarGroup>
      </TableCellCustom>
      <TableCellCustom flex>
        <CustomAvatarGroup max={3}>
          {teamMember?.length > 0 &&
            teamMember.map((team: any, index: number) => {
              if (team) {
                return (
                  <CustomAvatar
                    key={index}
                    name={team?.name}
                    color="info"
                    src={team?.profileImage}
                  />
                );
              }
              return null;
            })}
        </CustomAvatarGroup>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <Typography variant="body2">{row?.startDate}</Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <Typography variant="body2">{row?.endDate}</Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <TruncatedTypography variant="body2" text={row?.description} />
      </TableCellCustom>
      {(isManager || isSuperAdmin) && (
        <TableCellCustom flex align="center">
          <StatusTag status={row?.priority} />
        </TableCellCustom>
      )}
      {(isManager || isSuperAdmin) && (
        <TableCellCustom flex>
          <FormProvider methods={methods}>
            <RHFSelect
              name="updateStatus"
              value={statusValue}
              sx={{ width: '120px' }}
              SelectProps={{
                sx: {
                  fontSize: '14px',
                },
              }}
              size="small"
              onChange={(e) => {
                methods.setValue('updateStatus', e.target.value);
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ fontSize: '13px' }}
                  onClick={deleteDialog.show}
                >
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
          </FormProvider>
        </TableCellCustom>
      )}
      <DeleteDialog
        title={`${Strings.common.update} ${Strings.field.status}`}
        deleteButtonTitle={Strings.button.confirm}
        caption={Strings.common.updateCaption(updatedStatus)}
        loading={loading}
        visible={deleteDialog.visible}
        onClickDelete={onPressConfirm}
        onClose={deleteDialog.hide}
      />
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <StatusTag status={row?.status} />
      </TableCellCustom>
      {canAddProject ? (
        <TableCellCustom width={50} align="right">
          <RowActions row={row} />
        </TableCellCustom>
      ) : undefined}
    </>
  );
};

export { ProjectRow };
